/**
 * OCPP 1.6J adapter: builds outbound CALLs as version-neutral `OcppCall`
 * objects, parses inbound frames into `IncomingOcppEvent`s. The wire-level
 * field names live here and only here.
 *
 * The adapter itself doesn't move bytes — it hands the frame to a transport.
 * Tests inject a fake transport; production wires it to `OcppWebSocketTransport`.
 */
import { ConfigurationKeyType, ConfigurationSection } from '@/domain/types';
import { OcppValidationError } from '@/protocol/errors';
import {
  OCPP_MESSAGE_TYPE,
  type OcppCall,
  type OcppCallErrorFrame,
  type OcppCallResultFrame,
} from '@/protocol/OcppCall';
import type {
  AuthorizeResponse,
  BootNotificationDomain,
  BootNotificationResponse,
  ConfigurationKeyDescriptor,
  DataTransferDomain,
  DataTransferResponse,
  HeartbeatResponse,
  IncomingOcppEvent,
  MeterValuesDomain,
  OcppAdapter,
  StartTransactionDomain,
  StartTransactionResponse,
  StatusNotificationDomain,
  StopTransactionDomain,
  StopTransactionResponse,
} from '@/protocol/OcppAdapter';
import { validatorFor } from './schemas';
import { buildAuthorizeRequest, parseAuthorizeResponse } from './actions/authorize';
import {
  buildBootNotificationRequest,
  parseBootNotificationResponse,
} from './actions/bootNotification';
import { buildDataTransferRequest, parseDataTransferResponse } from './actions/dataTransfer';
import {
  buildFirmwareStatusNotificationRequest,
  buildDiagnosticsStatusNotificationRequest,
  type V16FirmwareStatus,
  type V16DiagnosticsStatus,
} from './actions/firmware';
import { buildHeartbeatRequest, parseHeartbeatResponse } from './actions/heartbeat';
import { buildMeterValuesRequest } from './actions/meterValues';
import {
  buildStartTransactionRequest,
  parseStartTransactionResponse,
} from './actions/startTransaction';
import { buildStatusNotificationRequest } from './actions/statusNotification';
import {
  buildStopTransactionRequest,
  parseStopTransactionResponse,
} from './actions/stopTransaction';
import { dispatchIncoming } from './actions/inbound';

export interface OcppCallSink {
  /** Send a CALL frame and return a promise that resolves on CALLRESULT for the same messageId. */
  call(
    action: string,
    payload: Record<string, unknown>,
    timeoutMs?: number,
  ): { messageId: string; response: Promise<Record<string, unknown>> };
}

export const SUPPORTED_FEATURE_PROFILES_V16 = [
  'Core',
  'SmartCharging',
  'RemoteTrigger',
  'LocalAuthListManagement',
] as const;

/** Mirrors evse2's DEFAULT_CONFIG set (minus obsolete LocalAuthList/Vendor keys). */
export const DEFAULT_CONFIGURATION_KEYS_V16: readonly ConfigurationKeyDescriptor[] = [
  // Core
  k('HeartbeatInterval', '300', false, ConfigurationKeyType.Integer, ConfigurationSection.Core),
  k(
    'MeterValuesAlignedData',
    'Energy.Active.Import.Register',
    false,
    ConfigurationKeyType.CSL,
    ConfigurationSection.Core,
  ),
  k(
    'MeterValuesSampledData',
    'Energy.Active.Import.Register,Power.Active.Import,SoC,Voltage,Current.Import',
    false,
    ConfigurationKeyType.CSL,
    ConfigurationSection.Core,
  ),
  k(
    'MeterValueSampleInterval',
    '60',
    false,
    ConfigurationKeyType.Integer,
    ConfigurationSection.Core,
  ),
  k(
    'ClockAlignedDataInterval',
    '0',
    false,
    ConfigurationKeyType.Integer,
    ConfigurationSection.Core,
  ),
  k('ConnectionTimeOut', '60', false, ConfigurationKeyType.Integer, ConfigurationSection.Core),
  k('NumberOfConnectors', '3', true, ConfigurationKeyType.Integer, ConfigurationSection.Core),
  k('ResetRetries', '0', false, ConfigurationKeyType.Integer, ConfigurationSection.Core),
  k(
    'StopTransactionOnEVSideDisconnect',
    'true',
    false,
    ConfigurationKeyType.Boolean,
    ConfigurationSection.Core,
  ),
  k(
    'StopTransactionOnInvalidId',
    'true',
    false,
    ConfigurationKeyType.Boolean,
    ConfigurationSection.Core,
  ),
  k(
    'StopTxnAlignedData',
    'Energy.Active.Import.Register',
    false,
    ConfigurationKeyType.CSL,
    ConfigurationSection.Core,
  ),
  k(
    'StopTxnSampledData',
    'Energy.Active.Import.Register',
    false,
    ConfigurationKeyType.CSL,
    ConfigurationSection.Core,
  ),
  k(
    'SupportedFeatureProfiles',
    SUPPORTED_FEATURE_PROFILES_V16.join(','),
    true,
    ConfigurationKeyType.CSL,
    ConfigurationSection.Core,
  ),
  k(
    'TransactionMessageAttempts',
    '3',
    false,
    ConfigurationKeyType.Integer,
    ConfigurationSection.Core,
  ),
  k(
    'TransactionMessageRetryInterval',
    '60',
    false,
    ConfigurationKeyType.Integer,
    ConfigurationSection.Core,
  ),
  k(
    'UnlockConnectorOnEVSideDisconnect',
    'true',
    false,
    ConfigurationKeyType.Boolean,
    ConfigurationSection.Core,
  ),
  k('WebSocketPingInterval', '0', false, ConfigurationKeyType.Integer, ConfigurationSection.Core),
  // SmartCharging
  k(
    'ChargeProfileMaxStackLevel',
    '3',
    true,
    ConfigurationKeyType.Integer,
    ConfigurationSection.SmartCharging,
  ),
  k(
    'ChargingScheduleAllowedChargingRateUnit',
    'Current,Power',
    true,
    ConfigurationKeyType.CSL,
    ConfigurationSection.SmartCharging,
  ),
  k(
    'ChargingScheduleMaxPeriods',
    '100',
    true,
    ConfigurationKeyType.Integer,
    ConfigurationSection.SmartCharging,
  ),
  k(
    'MaxChargingProfilesInstalled',
    '10',
    true,
    ConfigurationKeyType.Integer,
    ConfigurationSection.SmartCharging,
  ),
  k(
    'ConnectorSwitch3to1PhaseSupported',
    'false',
    true,
    ConfigurationKeyType.Boolean,
    ConfigurationSection.SmartCharging,
  ),
  // RemoteTrigger
  k(
    'AuthorizeRemoteTxRequests',
    'false',
    false,
    ConfigurationKeyType.Boolean,
    ConfigurationSection.Auth,
  ),
  // BUG-028 / Session 8f.15 — AuthCache (OCPP 1.6J §9.2): cached
  // idTag→AuthStatus responses so we can short-circuit subsequent
  // Authorize CALLs while the cache entry is fresh. Distinct from
  // LocalAuthList (which is CSMS-pushed via SendLocalList). Default
  // 'true' to mirror evse2; clearable by CSMS via ClearCache or by
  // toggling this key to 'false'.
  k(
    'AuthorizationCacheEnabled',
    'true',
    false,
    ConfigurationKeyType.Boolean,
    ConfigurationSection.Auth,
  ),
  // LocalAuthListManagement (SPEC-001, Session 8f.4)
  k(
    'LocalAuthListEnabled',
    'false',
    false,
    ConfigurationKeyType.Boolean,
    ConfigurationSection.Auth,
  ),
  k(
    'LocalAuthorizeOffline',
    'true',
    false,
    ConfigurationKeyType.Boolean,
    ConfigurationSection.Auth,
  ),
  // OCPP 1.6J §9.1 — when 'true' the CP authorizes from its Local Auth List
  // (and cache) WHILE ONLINE, before sending Authorize.req to the CSMS.
  // Distinct from LocalAuthorizeOffline, which governs the OFFLINE case.
  // Default 'false': by default every Authorize round-trips to the CSMS;
  // flip this on to simulate/test local-whitelist authorization while
  // connected.
  k(
    'LocalPreAuthorize',
    'false',
    false,
    ConfigurationKeyType.Boolean,
    ConfigurationSection.Auth,
  ),
  k(
    'LocalAuthListMaxLength',
    '1000',
    true,
    ConfigurationKeyType.Integer,
    ConfigurationSection.Auth,
  ),
  k(
    'SendLocalListMaxLength',
    '100',
    true,
    ConfigurationKeyType.Integer,
    ConfigurationSection.Auth,
  ),
  // Vendor extensions (Custom section)
  // ─────────────────────────────────────────────────────────────────────
  // FEAT-005 — default flushed offline sessions use N separate MeterValues
  // CALLs (matches evse2). Flip to "true" to batch all samples into a
  // single CALL with `meterValue: [...all]`. Toggled from the Settings tab.
  k(
    'Evse3.BatchOfflineMeterValues',
    'false',
    false,
    ConfigurationKeyType.Boolean,
    ConfigurationSection.Custom,
  ),
  // Session 13 / CR mimo-rozsahu #14 — chargePointVendor + chargePointModel
  // used to be hardcoded in the store's boot() path. Surfacing them as
  // configuration keys lets a tester emulate different vendors (e.g.
  // 'ABL EVSE', 'Alpitronic') without recompiling.
  k(
    'Evse3.ChargePointVendor',
    'VirtualCharger',
    false,
    ConfigurationKeyType.String,
    ConfigurationSection.Custom,
  ),
  k(
    'Evse3.ChargePointModel',
    'sim',
    false,
    ConfigurationKeyType.String,
    ConfigurationSection.Custom,
  ),
];

/** Configuration-key name for the FEAT-005 offline-flush MeterValues toggle. */
export const KEY_BATCH_OFFLINE_METER_VALUES = 'Evse3.BatchOfflineMeterValues';

/** Session 13 — chargePointVendor / chargePointModel boot frame fields. */
export const KEY_CP_VENDOR = 'Evse3.ChargePointVendor';
export const KEY_CP_MODEL = 'Evse3.ChargePointModel';

function k(
  key: string,
  value: string,
  readOnly: boolean,
  type: ConfigurationKeyType,
  section: ConfigurationSection,
): ConfigurationKeyDescriptor {
  return { key, value, readonly: readOnly, type, section };
}

export class OcppV16Adapter implements OcppAdapter {
  readonly subprotocol = 'ocpp1.6';
  readonly #sink: OcppCallSink;

  constructor(sink: OcppCallSink) {
    this.#sink = sink;
  }

  // -------- outbound --------

  bootNotification(
    req: BootNotificationDomain,
  ): OcppCall<BootNotificationDomain, BootNotificationResponse> {
    return this.#send(
      'BootNotification',
      req,
      buildBootNotificationRequest,
      parseBootNotificationResponse,
    );
  }

  heartbeat(): OcppCall<Record<string, never>, HeartbeatResponse> {
    return this.#send('Heartbeat', {}, buildHeartbeatRequest, parseHeartbeatResponse);
  }

  statusNotification(
    req: StatusNotificationDomain,
  ): OcppCall<StatusNotificationDomain, Record<string, never>> {
    return this.#send('StatusNotification', req, buildStatusNotificationRequest, () => ({}));
  }

  authorize(idTag: string): OcppCall<{ idTag: string }, AuthorizeResponse> {
    return this.#send(
      'Authorize',
      { idTag },
      () => buildAuthorizeRequest(idTag),
      parseAuthorizeResponse,
    );
  }

  startTransaction(
    req: StartTransactionDomain,
  ): OcppCall<StartTransactionDomain, StartTransactionResponse> {
    return this.#send(
      'StartTransaction',
      req,
      buildStartTransactionRequest,
      parseStartTransactionResponse,
    );
  }

  stopTransaction(
    req: StopTransactionDomain,
  ): OcppCall<StopTransactionDomain, StopTransactionResponse> {
    return this.#send(
      'StopTransaction',
      req,
      buildStopTransactionRequest,
      parseStopTransactionResponse,
    );
  }

  meterValues(req: MeterValuesDomain): OcppCall<MeterValuesDomain, Record<string, never>> {
    return this.#send('MeterValues', req, buildMeterValuesRequest, () => ({}));
  }

  dataTransfer(req: DataTransferDomain): OcppCall<DataTransferDomain, DataTransferResponse> {
    return this.#send('DataTransfer', req, buildDataTransferRequest, parseDataTransferResponse);
  }

  /**
   * Session 20 — OCPP 1.6J FirmwareManagement progress notifications. Both
   * are CALLs whose response payload is empty. Fired by the store's
   * UpdateFirmware / GetDiagnostics handlers as they simulate the lifecycle.
   */
  firmwareStatusNotification(
    status: V16FirmwareStatus,
  ): OcppCall<{ status: string }, Record<string, never>> {
    return this.#send(
      'FirmwareStatusNotification',
      { status },
      () => buildFirmwareStatusNotificationRequest(status),
      () => ({}),
    );
  }

  diagnosticsStatusNotification(
    status: V16DiagnosticsStatus,
  ): OcppCall<{ status: string }, Record<string, never>> {
    return this.#send(
      'DiagnosticsStatusNotification',
      { status },
      () => buildDiagnosticsStatusNotificationRequest(status),
      () => ({}),
    );
  }

  // -------- inbound --------

  parseIncoming(frame: unknown): IncomingOcppEvent {
    if (!Array.isArray(frame)) {
      return { kind: 'Unknown', messageId: '', action: '', rawPayload: {} };
    }
    const [type, messageId, action, payload] = frame as [
      number,
      string,
      string,
      Record<string, unknown>,
    ];
    if (type !== OCPP_MESSAGE_TYPE.CALL) {
      return {
        kind: 'Unknown',
        messageId: String(messageId),
        action: String(action ?? ''),
        rawPayload: {},
      };
    }
    // Session 11 / CR 2026-05-27 §1.3 — Ajv-validate inbound CALL
    // payloads before dispatch. The defensive casts inside
    // `dispatchIncoming` (e.g. `payload['idTag'] as string`) used to
    // silently accept malformed frames; the dispatcher would then
    // propagate UB-typed values into the store. We surface the schema
    // failure as `InvalidIncoming` so the handler can reply a
    // `PropertyConstraintViolation` CALLERROR (per CR §3.1) and the
    // Event Log shows the rejection.
    //
    // `validatorFor` returns a sentinel that fails with
    // "unknown action" for actions outside `SCHEMAS` — we treat that
    // case as `UnknownIncoming` (the existing fall-through) instead of
    // `Invalid`, so unsupported CSMS actions still get a polite
    // `NotImplemented` reply downstream.
    const wirePayload = payload ?? {};
    const v = validatorFor(action, 'request')(wirePayload);
    if (
      !v.valid &&
      !v.errors.some((e) => e.startsWith('unknown action'))
    ) {
      return {
        kind: 'Invalid',
        messageId,
        action: 'Invalid',
        originalAction: action,
        reason: v.errors.join('; '),
        rawPayload: wirePayload,
      };
    }
    return dispatchIncoming(messageId, action, wirePayload);
  }

  // -------- capabilities --------

  supportedConfigurationKeys(): readonly ConfigurationKeyDescriptor[] {
    return DEFAULT_CONFIGURATION_KEYS_V16;
  }

  supportedFeatureProfiles(): readonly string[] {
    return SUPPORTED_FEATURE_PROFILES_V16;
  }

  // -------- frame helpers --------

  /**
   * Per-action timeout overrides (ms). The CallDispatcher default is 30s
   * (see DEFAULT_CALL_TIMEOUT_MS); we shorten Authorize because the UI
   * blocks on the spinner and 30s feels broken to a tester. Long-running
   * server-initiated work (Heartbeat) is left at the default.
   */
  static readonly ACTION_TIMEOUTS_MS: Readonly<Record<string, number>> = {
    Authorize: 15_000,
  };

  /** Build, validate, send, then validate the response. */
  #send<TReq, TRes>(
    action: string,
    domainReq: TReq,
    build: (req: TReq) => Record<string, unknown>,
    parse: (payload: Record<string, unknown>) => TRes,
  ): OcppCall<TReq, TRes> {
    const wire = build(domainReq);
    const reqValid = validatorFor(action, 'request')(wire);
    if (!reqValid.valid) {
      throw new OcppValidationError(action, 'request', reqValid.errors);
    }
    const timeoutMs = OcppV16Adapter.ACTION_TIMEOUTS_MS[action];
    const sent = this.#sink.call(action, wire, timeoutMs);
    const response = sent.response.then((payload) => {
      const resValid = validatorFor(action, 'response')(payload);
      if (!resValid.valid) {
        throw new OcppValidationError(action, 'response', resValid.errors);
      }
      return parse(payload);
    });
    return { messageId: sent.messageId, action, request: domainReq, response };
  }

  /** Build a CALLRESULT frame the simulator sends as the response to a CSMS-initiated CALL. */
  static buildCallResult(messageId: string, payload: Record<string, unknown>): OcppCallResultFrame {
    return [OCPP_MESSAGE_TYPE.CALLRESULT, messageId, payload];
  }

  /** Build a CALLERROR frame for an unsupported / failed CSMS-initiated CALL. */
  static buildCallError(
    messageId: string,
    errorCode: string,
    errorDescription: string,
    details: Record<string, unknown> = {},
  ): OcppCallErrorFrame {
    return [OCPP_MESSAGE_TYPE.CALLERROR, messageId, errorCode, errorDescription, details];
  }
}
